import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, Phone, Mail, Globe, MapPin, Share2, Pencil, Trash2,
  Building2, RotateCw, StickyNote, Linkedin, Twitter, Instagram,
  Facebook, Github, Youtube, Link as LinkIcon, QrCode, Bell, Check, UserPlus, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { saveToPhoneContacts } from "../lib/saveContact";
import { toHref, detectSocial } from "../lib/links";
import { reminderState, formatDue } from "../lib/reminders";
import CardImage from "./CardImage";
import CardLightbox from "./CardLightbox";

const SOCIAL_ICONS = {
  linkedin: Linkedin, twitter: Twitter, instagram: Instagram,
  facebook: Facebook, github: Github, youtube: Youtube, link: LinkIcon,
};

const INDUSTRY_COLORS = {
  Technology: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  Finance: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Healthcare: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "Real Estate": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Legal: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Marketing: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  Construction: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};

const REMINDER_STYLES = {
  overdue: { chip: "bg-red-500/15 text-red-300 border-red-500/30", label: "Overdue" },
  today: { chip: "bg-blue-500/15 text-blue-300 border-blue-500/30", label: "Today" },
  upcoming: { chip: "bg-slate-500/15 text-slate-300 border-slate-500/30", label: "Upcoming" },
};

function initials(name, company) {
  const src = (name || company || "?").trim();
  const parts = src.split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

const iconBtn =
  "p-2 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition-colors";

export default function CardItem({ card, onShare, onEdit, onDelete, onToggleFav, onShowQR, onRemind, selectMode, selected, onToggleSelect }) {
  const [flipped, setFlipped] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveContact = async () => {
    setSaving(true);
    try {
      await saveToPhoneContacts(card);
      toast.success("Open the downloaded contact to add it to your phone");
    } catch {
      toast.error("Couldn't create the contact file");
    } finally {
      setSaving(false);
    }
  };
  const industryClass =
    INDUSTRY_COLORS[card.industry] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  const rState = reminderState(card);
  const rStyle = rState ? REMINDER_STYLES[rState] : null;

  const cardImages = [
    card.image_path && { path: card.image_path, label: "Front" },
    card.back_image_path && { path: card.back_image_path, label: "Back" },
  ].filter(Boolean);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      className={`card-3d ${selected ? "rounded-2xl ring-2 ring-blue-500 ring-offset-2 ring-offset-[#0B0F17]" : ""}`}
      data-testid="rolodex-card-item"
    >
      <div className={`card-flip-inner ${flipped ? "flipped" : ""}`} style={{ minHeight: 220 }}>
        {/* FRONT */}
        <div className="card-face">
          {selectMode && (
            <button
              data-testid="card-select-checkbox"
              onClick={() => onToggleSelect(card)}
              className="absolute inset-0 z-20 rounded-2xl flex items-start justify-start p-4 cursor-pointer bg-transparent"
              aria-label={selected ? "Deselect contact" : "Select contact"}
              aria-pressed={selected}
            >
              <span
                className={`w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors ${
                  selected ? "bg-blue-600 border-blue-600 text-white" : "bg-slate-900/70 border-slate-500"
                }`}
              >
                {selected && <Check className="w-4 h-4" />}
              </span>
            </button>
          )}
          <div className="relative h-full rounded-2xl p-5 bg-gradient-to-br from-[#1E293B] to-[#141d2e] border border-blue-500/15 shadow-[0_18px_40px_rgba(0,0,0,0.45)] overflow-hidden">
            <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-blue-500/5 blur-2xl" />
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-600/90 flex items-center justify-center font-heading font-bold text-white text-lg shadow-lg">
                {initials(card.name, card.company)}
              </div>
              <div className="flex items-center gap-2">
                {cardImages.length > 0 && (
                  <button
                    data-testid="card-photo-thumbnail"
                    onClick={() => setLightbox(true)}
                    title="View card photo"
                    className="relative w-14 h-9 rounded-md overflow-hidden border border-slate-600/70 hover:border-blue-500 transition-colors shadow-md group/thumb"
                  >
                    <CardImage path={cardImages[0].path} className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/20 transition-colors" />
                    {cardImages.length > 1 && (
                      <span className="absolute bottom-0 right-0 text-[8px] font-mono px-1 bg-blue-600 text-white rounded-tl">
                        2
                      </span>
                    )}
                  </button>
                )}
                {rStyle && (
                  <span
                    data-testid="card-reminder-badge"
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${rStyle.chip}`}
                  >
                    <Bell className="w-3 h-3" /> {rStyle.label}
                  </span>
                )}
                <button
                  data-testid="favorite-toggle"
                  onClick={() => onToggleFav(card)}
                  className="text-slate-500 hover:text-blue-400 transition-colors"
                  aria-label="Favorite"
                >
                  <Star className={`w-5 h-5 ${card.favorite ? "fill-blue-400 text-blue-400" : ""}`} />
                </button>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="font-heading text-xl font-semibold tracking-tight text-white leading-snug">
                {card.name || "Unknown"}
              </h3>
              {card.title && <p className="text-sm text-slate-400 mt-0.5">{card.title}</p>}
              {card.company && (
                <p className="flex items-center gap-1.5 text-sm text-slate-300 mt-2">
                  <Building2 className="w-4 h-4 text-blue-500/80" /> {card.company}
                </p>
              )}
            </div>

            <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between">
              {card.industry ? (
                <span className={`text-[11px] px-2.5 py-1 rounded-full border ${industryClass}`}>
                  {card.industry}
                </span>
              ) : <span />}
              <button
                data-testid="card-flip-button"
                onClick={() => setFlipped(true)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-400 transition-colors"
              >
                <RotateCw className="w-3.5 h-3.5" /> Details
              </button>
            </div>
          </div>
        </div>

        {/* BACK */}
        <div className="card-face card-back">
          <div className="relative h-full rounded-2xl p-5 bg-gradient-to-br from-[#0f1826] to-[#1E293B] border border-slate-700 shadow-[0_18px_40px_rgba(0,0,0,0.45)] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="overline text-blue-500">Contact</span>
              <button
                onClick={() => setFlipped(false)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-400 transition-colors"
              >
                <RotateCw className="w-3.5 h-3.5" /> Back
              </button>
            </div>

            <div className="space-y-2.5 text-sm flex-1">
              {card.phone && (
                <a data-testid="contact-phone-link" href={`tel:${card.phone}`} className="flex items-center gap-2.5 text-slate-300 hover:text-blue-400 transition-colors">
                  <Phone className="w-4 h-4 text-blue-500/80 shrink-0" /> <span className="truncate">{card.phone}</span>
                </a>
              )}
              {card.phone2 && (
                <a data-testid="contact-phone2-link" href={`tel:${card.phone2}`} className="flex items-center gap-2.5 text-slate-300 hover:text-blue-400 transition-colors">
                  <Phone className="w-4 h-4 text-blue-500/50 shrink-0" /> <span className="truncate">{card.phone2}</span>
                </a>
              )}
              {card.email && (
                <a data-testid="contact-email-link" href={`mailto:${card.email}`} className="flex items-center gap-2.5 text-slate-300 hover:text-blue-400 transition-colors">
                  <Mail className="w-4 h-4 text-blue-500/80 shrink-0" /> <span className="truncate">{card.email}</span>
                </a>
              )}
              {card.email2 && (
                <a data-testid="contact-email2-link" href={`mailto:${card.email2}`} className="flex items-center gap-2.5 text-slate-300 hover:text-blue-400 transition-colors">
                  <Mail className="w-4 h-4 text-blue-500/50 shrink-0" /> <span className="truncate">{card.email2}</span>
                </a>
              )}
              {card.website && (
                <a data-testid="contact-website-link" href={toHref(card.website)} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-slate-300 hover:text-blue-400 transition-colors">
                  <Globe className="w-4 h-4 text-blue-500/80 shrink-0" /> <span className="truncate">{card.website}</span>
                </a>
              )}
              {card.address && (
                <p className="flex items-start gap-2.5 text-slate-400">
                  <MapPin className="w-4 h-4 text-blue-500/80 shrink-0 mt-0.5" /> <span className="line-clamp-2">{card.address}</span>
                </p>
              )}
              {card.notes && (
                <p className="flex items-start gap-2.5 text-slate-400 italic">
                  <StickyNote className="w-4 h-4 text-blue-500/80 shrink-0 mt-0.5" /> <span className="line-clamp-2">{card.notes}</span>
                </p>
              )}
              {rStyle && (
                <p className={`flex items-start gap-2.5 ${rState === "overdue" ? "text-red-300" : rState === "today" ? "text-blue-300" : "text-slate-400"}`}>
                  <Bell className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="line-clamp-2">Follow up {formatDue(card.reminder_at)}{card.reminder_note ? ` — ${card.reminder_note}` : ""}</span>
                </p>
              )}
              {(card.social_links || []).length > 0 && (
                <div className="flex items-center flex-wrap gap-2 pt-1">
                  {card.social_links.map((link, i) => {
                    const meta = detectSocial(link);
                    const Icon = SOCIAL_ICONS[meta.key] || LinkIcon;
                    return (
                      <a
                        key={i}
                        href={toHref(link)}
                        target="_blank"
                        rel="noreferrer"
                        title={meta.label}
                        data-testid="social-link"
                        className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 hover:text-blue-400 hover:border-blue-500 transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-3 mt-2 border-t border-slate-700/70">
              <button data-testid="share-contact-button" onClick={() => onShare(card)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors">
                <Share2 className="w-4 h-4" /> Share
              </button>
              <button data-testid="save-to-contacts-button" onClick={saveContact} disabled={saving} className={iconBtn} title="Save to phone contacts">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              </button>
              <button data-testid="qr-contact-button" onClick={() => onShowQR(card)} className={iconBtn} title="Show QR code">
                <QrCode className="w-4 h-4" />
              </button>
              <button data-testid="remind-contact-button" onClick={() => onRemind(card)} className={`${iconBtn} ${rStyle ? "text-blue-400 border-blue-500/50" : ""}`} title="Set follow-up">
                <Bell className="w-4 h-4" />
              </button>
              <button data-testid="edit-contact-button" onClick={() => onEdit(card)} className={iconBtn}>
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid="delete-contact-button" onClick={() => onDelete(card)} className="p-2 rounded-lg border border-slate-700 text-slate-300 hover:text-red-400 hover:border-red-500/50 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <CardLightbox
        open={lightbox}
        onClose={() => setLightbox(false)}
        images={cardImages}
        title={card.name || card.company}
      />
    </motion.div>
  );
}
